import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Dialog } from '../../components/Dialog';
import { FuzzyDateInput } from '../../components/FuzzyDateInput';
import { Icon } from '../../components/Icon';
import { useAnnouncer } from '../../components/Status';
import { MILESTONE_KINDS, type FuzzyDate, type MilestoneKind } from '../../domain/types';
import { useI18n } from '../../i18n';
import { preparePhoto } from '../../lib/image';
import { uuid } from '../../lib/uuid';
import { useRepos } from '../../repositories';
import { useApp } from '../../app/AppState';
import { MILESTONE_ICONS } from './milestoneIcons';
import { takePrefill } from '../../assistant/prefill';

export type AddMomentKind = 'photo' | 'event' | 'milestone';

/**
 * Share a photo, add an everyday event (title required, picture optional),
 * or add a family milestone — a simcha or a big moment, chosen from a list
 * first (wedding, bar mitzvah, a birth…) so the family always knows what it
 * is. Opened from the Add tab in the dock, so it can appear over any screen;
 * the picture is chosen from inside the dialog (a tap on "choose a picture"),
 * which keeps the file picker on a real user gesture wherever the dialog was
 * opened from.
 */
export function AddMomentDialog({ kind, onClose, onSaved }: { kind: AddMomentKind | null; onClose: () => void; onSaved?: () => void }) {
  const { t } = useI18n();
  const repos = useRepos();
  const { toast } = useAnnouncer();
  const { reload, openMemories } = useApp();
  const [pending, setPending] = useState<{ blob: Blob; width: number; height: number; url: string } | null>(null);
  const [title, setTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [isEvent, setIsEvent] = useState(false);
  const [milestone, setMilestone] = useState<MilestoneKind | null>(null);
  const [takenOn, setTakenOn] = useState<FuzzyDate>({ kind: 'unknown' });
  const [busy, setBusy] = useState(false);
  const pickRef = useRef<HTMLInputElement | null>(null);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;

  // A fresh form every time the dialog opens — unless the assistant has just
  // typed something into it on the person's behalf, in which case it opens with
  // their own words in it and they press Save themselves. See assistant/prefill.
  useEffect(() => {
    if (!kind) return;
    const filled = takePrefill(kind === 'photo' ? 'add.photo' : kind === 'event' ? 'add.event' : 'add.milestone');
    setTitle(filled?.title ?? '');
    setCaption(filled?.caption ?? filled?.notes ?? '');
    setIsEvent(kind === 'event' || kind === 'milestone');
    setMilestone(null);
    setTakenOn({ kind: 'unknown' });
    setPending(null);
    return () => {
      if (pendingRef.current) URL.revokeObjectURL(pendingRef.current.url);
      setPending(null);
    };
  }, [kind]);

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const prepared = await preparePhoto(file);
      if (pending) URL.revokeObjectURL(pending.url);
      setPending({ ...prepared, url: URL.createObjectURL(prepared.blob) });
    } catch {
      toast(t('portrait.failed'));
    } finally {
      setBusy(false);
    }
  };

  const isMilestone = kind === 'milestone';
  const save = async () => {
    const event = kind === 'event' || isMilestone || isEvent;
    if (!pending && !(event && title.trim())) return;
    if (isMilestone && !milestone) return;
    setBusy(true);
    const res = await repos.photos.add({
      image: pending?.blob, width: pending?.width, height: pending?.height, title: event ? title : undefined, caption, isEvent: event,
      milestone: isMilestone && milestone ? milestone : undefined,
      takenOn: takenOn.kind === 'unknown' ? undefined : takenOn, idempotencyKey: uuid(),
    });
    setBusy(false);
    if (res.ok) {
      toast(isMilestone ? t('photos.savedMilestone') : event ? t('photos.savedEvent') : t('photos.saved'));
      reload();
      onSaved?.();
      onClose();
      // A new simcha opens straight away, so the person who created it can start
      // filling it — and so everyone else can see that is what it is for.
      if (isMilestone) openMemories({ kind: 'moment', id: res.value.id });
    } else {
      toast(t(('messageKey' in res ? res.messageKey : 'error.unexpected') as Parameters<typeof t>[0]));
    }
  };

  const isEventKind = kind === 'event' || isMilestone;
  const dialogTitle = isMilestone ? t('photos.addMilestoneTitle') : kind === 'event' ? t('photos.addEventTitle') : t('photos.addTitle');
  const milestoneLabel = (k: MilestoneKind) => t(`milestone.${k}` as 'milestone.wedding');
  // A milestone starts by saying which kind it is; the form comes after.
  const choosing = isMilestone && !milestone;
  return (
    <Dialog
      open={kind !== null}
      onClose={onClose}
      title={dialogTitle}
      labelledBy="photo-add-title"
      actions={
        choosing ? undefined : (
          <button type="button" className="btn btn-primary btn-lg" onClick={() => void save()} disabled={busy || (isEventKind ? !title.trim() : !pending)}>
            <Icon name="check" />
            <span>{isMilestone ? t('photos.shareMilestone') : t('photos.share')}</span>
          </button>
        )
      }
    >
      <input ref={pickRef} type="file" accept="image/*" hidden onChange={(e) => void onFile(e)} />
      {choosing ? (
        <div className="stack">
          <p className="muted" style={{ margin: 0 }}>{t('photos.milestoneWhich')}</p>
          <div className="milestone-grid" role="list">
            {MILESTONE_KINDS.map((k: MilestoneKind) => (
              <button
                key={k}
                type="button"
                role="listitem"
                className={`milestone-choice ${k === 'other' ? 'is-other' : ''}`}
                onClick={() => setMilestone(k)}
              >
                <span className="milestone-choice-icon" aria-hidden="true"><Icon name={MILESTONE_ICONS[k]} /></span>
                <span>{milestoneLabel(k)}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="stack-lg">
          {isMilestone && milestone && (
            <div className="milestone-picked">
              <span className="chip chip-milestone"><Icon name={MILESTONE_ICONS[milestone]} />{milestoneLabel(milestone)}</span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMilestone(null)}>{t('photos.milestoneChange')}</button>
            </div>
          )}
          {pending ? (
            <div className="stack">
              <img className="photo-preview" src={pending.url} alt="" />
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => pickRef.current?.click()} disabled={busy}>
                <Icon name="image" />
                <span>{t('photos.changePicture')}</span>
              </button>
            </div>
          ) : (
            <button type="button" className="photo-pick" onClick={() => pickRef.current?.click()} disabled={busy}>
              <Icon name={isEventKind ? 'image' : 'camera'} />
              <span>{isEventKind ? t('photos.eventPicture') : t('photos.choosePicture')}</span>
            </button>
          )}
          {isEventKind && (
            <div className="field">
              <label className="field-label" htmlFor="event-title">{isMilestone ? t('photos.milestoneTitle') : t('photos.eventTitle')}</label>
              <input
                id="event-title"
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={isMilestone && milestone ? t(`milestone.hint.${milestone}` as 'milestone.hint.wedding') : t('photos.eventTitleHint')}
              />
            </div>
          )}
          <div className="field">
            <label className="field-label" htmlFor="photo-caption">{isEventKind ? t('photos.eventNotes') : t('photos.caption')} <span className="faint">({t('common.optional')})</span></label>
            <input id="photo-caption" className="input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={isEventKind ? t('photos.eventNotesHint') : t('photos.captionHint')} />
          </div>
          {!isEventKind && (
            <label className={`radio-card ${isEvent ? 'selected' : ''}`}>
              <input type="checkbox" checked={isEvent} onChange={(e) => setIsEvent(e.target.checked)} />
              <span className="title">{t('photos.isEvent')}</span>
              <span className="small muted">{t('photos.isEventHelp')}</span>
            </label>
          )}
          {!isEventKind && isEvent && (
            <div className="field">
              <label className="field-label" htmlFor="event-title-2">{t('photos.eventTitle')}</label>
              <input id="event-title-2" className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('photos.eventTitleHint')} />
            </div>
          )}
          <FuzzyDateInput value={takenOn} onChange={setTakenOn} kinds={['exact', 'month', 'year', 'unknown']} label={isEventKind ? t('photos.eventWhen') : t('photos.when')} compact defaultToToday />
        </div>
      )}
    </Dialog>
  );
}
